import { useTranslation } from 'module/hooks/use-translation';
import { Button } from 'module/ui/components/atoms/button';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  ExpandableSearchInput,
  ExpandableSearchInputRef,
} from 'module/ui/components/atoms/input/search-input';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { HeaderWithIconTitle } from 'module/ui/components/molecules/page/page';
import { Argon } from 'module/ui/components/templates/argon';
import {
  faPlus,
  // faChevronDown,
  faFileLines,
} from '@fortawesome/pro-regular-svg-icons';
// import { faBook } from '@fortawesome/pro-solid-svg-icons';
import { PageTemplateLite } from 'api/page-template/types';
import {
  useGetAllCompanyPageTemplatesQuery,
  useUpdateCompanyPageTemplateOrderMutation,
  useDeleteCompanyPageTemplateMutation,
} from 'queries/company-page-template';
import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from 'routes/route-paths';
// import styled from 'styled-components';
import { FlexWrapperWithShortGap } from 'components/common/styles';
import { CompanyTemplateTable } from 'components/organisms/company-page-template/list-table/table';
import {
  CompanyPageTemplatesNoSearchMatchState,
  CompanyPageTemplatesNoTemplatesState,
} from './empty-state';
import { ContentWrapper, ListLoader } from '../../templates/styles';
import type { CompanyPageTemplatePermissions } from 'types/nitro-pages';

interface CompanyPageTemplatesProps {
  companyPageTemplatePermissions?: CompanyPageTemplatePermissions;
}

// const StyledBookIcon = styled(FontAwesomeIcon)`
//   color: var(--icon-color);
// `;

export const CompanyPageTemplates = ({
  companyPageTemplatePermissions,
}: CompanyPageTemplatesProps = {}) => {
  const canWrite = companyPageTemplatePermissions?.canWrite !== false;
  const canDelete = companyPageTemplatePermissions?.canDelete !== false;
  // Write permission allows creating templates (same as meeting)
  const canCreate = canWrite;
  const pageTitle = useTranslation('company_page_templates.title');
  const navigate = useNavigate();
  const { value: searchQuery, onChange: onSearchChange } =
    useSearchValueState();
  const searchInputRef = useRef<ExpandableSearchInputRef>(null);

  const { data: apiTemplates, isLoading } =
    useGetAllCompanyPageTemplatesQuery();
  const [templates, setTemplates] = React.useState<PageTemplateLite[]>([]);
  const deleteCompanyPageTemplateMutation =
    useDeleteCompanyPageTemplateMutation();
  const updateCompanyPageTemplateOrderMutation =
    useUpdateCompanyPageTemplateOrderMutation();

  React.useEffect(() => {
    if (apiTemplates) {
      setTemplates(apiTemplates);
    }
  }, [apiTemplates]);

  const filteredTemplates = React.useMemo(() => {
    if (!searchQuery) {
      return templates;
    }

    return templates.filter((template) =>
      template.templateName.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [templates, searchQuery]);

  const handleDelete = async (id: string) => {
    try {
      await deleteCompanyPageTemplateMutation.mutateAsync(Number(id));
    } catch (error) {
      // toast.error(error.message);
      // eslint-disable-next-line no-console
      console.error(error);
    }
    // setTemplates(
    //   templates.filter((template) => String(template.templateId) !== id),
    // );
  };

  const handleReorder = async (
    newTemplates: PageTemplateLite[],
    movedTemplateId: number,
    newIndex: number,
  ) => {
    try {
      setTemplates(newTemplates);
      await updateCompanyPageTemplateOrderMutation.mutateAsync({
        templateId: movedTemplateId,
        order: newIndex,
      });
    } catch (error) {
      // toast.error(error.message);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  return (
    <Argon
      documentTitle={pageTitle}
      title={
        <HeaderWithIconTitle
          icon={<FontAwesomeIcon icon={faFileLines} size={20} />}
          title={pageTitle}
        />
      }
      description={<FormattedMessage id="company_page_templates.description" />}
      actions={{
        secondary: (
          <FlexWrapperWithShortGap>
            <ExpandableSearchInput
              ref={searchInputRef}
              direction="right"
              value={searchQuery}
              onChange={onSearchChange}
              searchButtonProps={{
                type: 'text',
              }}
              minWidth="360px"
            />
            {/* <Button>
              <FlexWrapperWithShortGap>
                <StyledBookIcon icon={faBook} />
                <FontAwesomeIcon icon={faChevronDown} />
              </FlexWrapperWithShortGap>
            </Button> */}

            {canCreate && (
              <Button
                icon={<FontAwesomeIcon icon={faPlus} size={12} />}
                type="primary"
                onClick={() => navigate(ROUTE_PATHS.NEW)}
              >
                <FormattedMessage id="new_template" />
              </Button>
            )}
          </FlexWrapperWithShortGap>
        ),
      }}
      content={
        isLoading ? (
          <ListLoader />
        ) : (
          <ContentWrapper>
            {(apiTemplates?.length ?? 0) === 0 ? (
              <CompanyPageTemplatesNoTemplatesState
                onPrimaryAction={
                  canCreate ? () => navigate(ROUTE_PATHS.NEW) : undefined
                }
              />
            ) : filteredTemplates.length === 0 ? (
              <CompanyPageTemplatesNoSearchMatchState
                onPrimaryAction={() =>
                  searchInputRef.current?.closeSearchInput()
                }
              />
            ) : (
              <CompanyTemplateTable
                templates={filteredTemplates}
                onEdit={
                  canWrite ? (id: string) => navigate(`${id}`) : undefined
                }
                onDelete={canDelete ? handleDelete : undefined}
                onReorder={canWrite ? handleReorder : undefined}
                loading={isLoading}
              />
            )}
          </ContentWrapper>
        )
      }
    />
  );
};
